import { useCallback } from 'react'
import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { ReferencedEntityWithIcon } from '../../../../domain/value-object/ReferencedEntity'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { useWbsItemRepository } from '../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { useRootWbsItems } from './useRootWbsItems'
import { pruneRootUuids } from '../wbsItemSearch'

type ExcludeResult = {
  cleaned: WbsItemSearchConditionV2VO
  excluded: boolean
}

// 削除されたWBSが rootUuids に残っているとバックエンドが NotFoundException を投げて
// 検索が継続的に失敗するため、検索条件適用時に有効な rootUuid だけに絞り込む。
// 成果物を含まない軽い一覧で確認し、そこに無いもの（成果物単体である可能性が
// ある分）だけを個別に確認する。常時成果物込みの重い一覧を取得すると、
// 成果物モードを使わないユーザーにも毎回コストがかかってしまうため。
export const useExcludeInaccessibleRootUuids = (
  functionUuid: string,
  projectUuid: string
) => {
  const getRootWbsItems = useRootWbsItems(projectUuid, false)
  const { fetchAsReferencedEntityByUuid } = useWbsItemRepository()

  const exclude = useCallback(
    async (condition: WbsItemSearchConditionV2VO): Promise<ExcludeResult> => {
      if (!condition.rootUuids || condition.rootUuids.length === 0) {
        return { cleaned: condition, excluded: false }
      }
      try {
        const validItems = await getRootWbsItems()
        const validUuids = new Set(validItems.map(item => item.uuid))
        const missingUuids = condition.rootUuids.filter(
          uuid => !validUuids.has(uuid)
        )

        if (missingUuids.length === 0) {
          return { cleaned: condition, excluded: false }
        }

        // 軽い一覧に無いものだけ、成果物単体である可能性があるため個別に確認する
        const results = await Promise.allSettled(
          missingUuids.map(uuid =>
            fetchAsReferencedEntityByUuid(projectUuid, uuid)
          )
        )
        const confirmedUuids = results
          .filter(
            (
              r
            ): r is PromiseFulfilledResult<
              ReferencedEntityWithIcon | undefined
            > => r.status === 'fulfilled'
          )
          .map(r => r.value)
          .filter((v): v is ReferencedEntityWithIcon => !!v)
          .map(v => v.uuid)

        const { pruned, changed } = pruneRootUuids(
          condition.rootUuids,
          new Set([...validUuids, ...confirmedUuids])
        )
        if (!changed) {
          return { cleaned: condition, excluded: false }
        }
        return {
          cleaned: { ...condition, rootUuids: pruned },
          excluded: true,
        }
      } catch {
        // 検証用APIが失敗した場合は元の条件で続行する（フェイルオープン）。
        // 削除済みUUIDが残っていれば検索時に従来通りのエラーが出るが、
        // 検証API障害で画面そのものを使えなくするほうが弊害が大きい。
        return { cleaned: condition, excluded: false }
      }
    },
    [getRootWbsItems, fetchAsReferencedEntityByUuid, projectUuid]
  )

  const notify = useCallback(() => {
    store.dispatch(
      addScreenMessage(functionUuid, {
        type: MessageLevel.WARN,
        title: intl.formatMessage({
          id: 'wbs.search.rootUuids.excluded.title',
        }),
      })
    )
  }, [functionUuid])

  return { exclude, notify }
}
